import update from 'immutability-helper';
import { createAction, createTypes } from '../../common/reduxHelpers';
import {
  toggleEmailNotification as toggleEmailNotificationAPI,
  addOrganizationToUser,
  putFollowedOrganizations,
  putOwnPhoneNumber,
} from '~services/api.helpers';
import { addToast } from '~features/toast/ducks';
import { setShow as setModalShow } from '~features/modals/ducks';
import { toggleDrawer } from '~features/drawer/ducks';

// Action types
export const USER = createTypes('USER', [
  'ADD',
  'SET_ORGANIZATION',
  'SET_FOLLOWED_ORGANIZATIONS',
  'RECEIVE_ORGANIZATIONS',
  'RECEIVE_NOTIFICATIONS',
  'RECEIVE_PHONE',
]);

// State
export const initialState = {
  data: null,
  organizations: [],
};

const loadGiosgScript = () => {
  // const bodyEmd = document.getElementsByTagName('body')[0];
  const fmJs = document.createElement('script');
  fmJs.src = 'https://pirha.echat.fi/pirha_saara.js';
  fmJs.setAttribute('id', '66fcfda7e1e06a00076a0ba1');
  fmJs.setAttribute('class', 'embedScript');
  fmJs.setAttribute('async', 'true');
  fmJs.setAttribute('data-auto-open', 'false');
  fmJs.setAttribute('data-custom-css', 'true');
  fmJs.setAttribute('data-label', 'CHAT');
  fmJs.setAttribute('data-language', 'fi');
  fmJs.setAttribute('data-client', '169');
  document.head.appendChild(fmJs);
};

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case USER.ADD: {
      // Only load Giosg script when user is first initialized
      if (action.payload.showChat && !state.data && !window.cordova) {
        loadGiosgScript(action.payload.name);
      }

      return update(state, {
        data: { $set: action.payload },
      });
    }
    case USER.SET_ORGANIZATION:
      return update(state, {
        data: {
          ordererOrganization: { $set: action.payload },
        },
      });
    case USER.SET_FOLLOWED_ORGANIZATIONS:
      return update(state, {
        data: {
          followedOrganizations: { $set: action.payload },
        },
      });
    case USER.RECEIVE_ORGANIZATIONS:
      return update(state, {
        organizations: { $set: action.payload },
      });
    case USER.RECEIVE_NOTIFICATIONS:
      return update(state, {
        notifications: { $set: action.payload },
      });
    case USER.RECEIVE_PHONE:
      return update(state, {
        data: {
          phone: { $set: action.payload },
        },
      });
    default:
      return state;
  }
}

// Actions
export const addUser = createAction(USER.ADD);
export const setUserOrganization = createAction(USER.SET_ORGANIZATION);
export const receiveUserOrganization = createAction(USER.RECEIVE_ORGANIZATIONS);
export const setFollowedOrganizations = createAction(
  USER.SET_FOLLOWED_ORGANIZATIONS
);
export const receivePhone = createAction(USER.RECEIVE_PHONE);
export const receiveNotifications = createAction(USER.RECEIVE_NOTIFICATIONS);

// Selectors
export const getUser = state => state.user.data;
export const getUserEmail = state => state.user.data.email;
export const getUserOrganization = state => state.user.organizations;
export const getUserOrdererOrganizationId = state =>
  state.user.data.ordererOrganization.id;
export const getUserOrdererOrganizationPhone = state =>
  state.user.data.ordererOrganization.telephone;
export const getUserFollowedOrganizations = state =>
  state.user.data.followedOrganizations;
export const getUserSubType = state =>
  state.user.data.ordererOrganization.subType;

// Thunks
export function toggleEmailNotification() {
  return async (dispatch, getState) => {
    try {
      const user = getUser(getState());
      const isEnabled = await toggleEmailNotificationAPI(
        !user.emailNotifications
      );
      dispatch(
        addUser({
          ...user,
          emailNotifications: isEnabled,
        })
      );
    } catch (e) {
      console.error('toggleError', e);
    }
  };
}

export function selectUserOrganization(organizationId) {
  return async (dispatch, getState) => {
    try {
      const user = getUser(getState());
      const org = await addOrganizationToUser(organizationId);

      // NOTE: If user doesn't have org set at all we want to reload page
      // to do init phase from scratch again.
      if (!user.ordererOrganization) location.reload();
      else dispatch(setUserOrganization(org));
    } catch (e) {
      console.error('Failed to set user organization', e);
    }
  };
}

export function addFollowedOrganization(organizationId) {
  return async (dispatch, getState) => {
    try {
      const oldOrgs = (await getUserFollowedOrganizations(getState())) || [];
      const newOrgs = [...oldOrgs, { id: parseInt(organizationId, 10) }];

      const resOrgs = await putFollowedOrganizations(newOrgs);

      dispatch(setFollowedOrganizations(resOrgs));
    } catch (e) {
      console.error('Failed to add user followed organization', e);
    }
  };
}

export function removeFollowedOrganization(organizationId) {
  return async (dispatch, getState) => {
    try {
      const oldOrgs = (await getUserFollowedOrganizations(getState())) || [];
      const newOrgs = oldOrgs.filter(org => org.id !== organizationId);

      const resOrgs = await putFollowedOrganizations(newOrgs);

      dispatch(setFollowedOrganizations(resOrgs));
    } catch (e) {
      console.error('Failed to remove user followed organization', e);
    }
  };
}

export function setPhone(phone) {
  return async dispatch => {
    try {
      const res = await putOwnPhoneNumber({ phone });
      dispatch(receivePhone(res.phone));
      dispatch(
        addToast({ type: 'SUCCESS', message: 'Puhelinnumero päivitetty' })
      );
      dispatch(setModalShow(null));
      dispatch(toggleDrawer('profile'));
    } catch (e) {
      console.error('Failed to update user phone number', e);
      dispatch(
        addToast({
          type: 'SUCCESS',
          message: 'Puhelinnumeron päivitys epäonnistui',
        })
      );
    }
  };
}
